import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    CircularProgress
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import DescriptionIcon from "@material-ui/icons/Description";
import { Storage } from "aws-amplify";
import agent from "../../../agent";
import "./ActDocumentsModal.css";

@inject("templateStore")
@observer
class ActDocumentsModal extends Component {
    state = {
        documents: [],
        loading: true,
        error: null
    };

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open && this.props.act) {
            this.loadDocuments();
        }
    }

    loadDocuments = async () => {
        this.setState({ loading: true, error: null });
        
        try {
            const actAdminId = this.props.act?.myMusician?.id || this.props.act?.actAdmin?.id || this.props.act?.actAdminId;

            if (!actAdminId) {
                this.setState({ documents: [], loading: false });
                return;
            }

            const docResponse = await agent.Document.getByProfile(actAdminId);

            const docs = docResponse && docResponse.documents ? docResponse.documents : (Array.isArray(docResponse) ? docResponse : []);
            const documents = docs
                // .filter(doc => doc.documentType !== 'insurance' && doc.documentType !== 1)
                .map(doc => ({
                    id: doc.guid,
                    title: doc.title || doc.fileName,
                    fileName: doc.fileName,
                    fileContent: doc.fileContent,
                    fileContentUrl: doc.fileContentUrl || doc.fileContent,
                    fileGuid: doc.guid,
                    mime: doc.mime,
                    createdAt: doc.createdAt
                }));

            this.setState({ documents, loading: false });
        } catch (error) {
            console.error("Error loading documents:", error);
            this.setState({ 
                error: "Failed to load documents", 
                loading: false,
                documents: []
            });
        }
    };

    handleViewDocument = async (document) => {
        try {
            const url = await Storage.get(document.fileContentUrl || document.fileContent);
            window.open(url, '_blank');
        } catch (error) {
            console.error("Error viewing document:", error);
            this.props.templateStore.openSnackbar("Failed to open document");
        }
    };

    handleDownloadDocument = async (document) => {
        try {
            const url = await Storage.get(document.fileContentUrl || document.fileContent);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = document.fileName || 'document';
            link.target = '_blank';
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
        } catch (error) {
            console.error("Error downloading document:", error);
            this.props.templateStore.openSnackbar("Failed to download document");
        }
    };

    handleDownloadAll = async () => {
        const { documents } = this.state;
        const blobUrls = [];
        for (const doc of documents) {
            try {
                const result = await Storage.get(doc.fileContentUrl || doc.fileContent, { download: true });
                const blob = result.Body instanceof Blob ? result.Body : new Blob([result.Body], { type: result.ContentType || 'application/octet-stream' });
                const blobUrl = URL.createObjectURL(blob);
                blobUrls.push(blobUrl);
                const link = window.document.createElement('a');
                link.href = blobUrl;
                link.download = doc.fileName || 'document';
                window.document.body.appendChild(link);
                link.click();
                window.document.body.removeChild(link);
                await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (error) {
                console.error("Error downloading document:", error);
                this.props.templateStore.openSnackbar(`Failed to download ${doc.fileName || 'document'}`);
            }
        }
        blobUrls.forEach(url => URL.revokeObjectURL(url));
    };

    getFileIcon(fileName) {
        if (!fileName) return "fa-file";
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf':
                return "fa-file-pdf";
            case 'doc':
            case 'docx':
                return "fa-file-word";
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return "fa-file-image";
            default:
                return "fa-file";
        }
    }

    getFileIconColor(fileName) {
        if (!fileName) return "#666";
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf':
                return "#e53935";
            case 'doc':
            case 'docx':
                return "#1976d2";
            default:
                return "#666";
        }
    }

    formatFileSize(bytes) {
        if (!bytes) return "";
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    }

    render() {
        const { open, onClose, act } = this.props;
        const { documents, loading, error } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                className="act-documents-modal"
            >
                <DialogTitle disableTypography className="modal-title">
                    <div className="title-content">
                        <FolderOpenIcon className="title-icon" />
                        <span>Documents for Act Owner - {act?.actName || 'Act'}</span>
                    </div>
                    <IconButton onClick={onClose} className="close-btn">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="modal-content">
                    {loading ? (
                        <div className="loading-state">
                            <CircularProgress />
                            <Typography variant="body1">Loading documents...</Typography>
                        </div>
                    ) : error ? (
                        <div className="error-state">
                            <Typography color="error">{error}</Typography>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="empty-state">
                            <DescriptionIcon className="empty-icon" />
                            <Typography variant="h6">No Documents</Typography>
                            <Typography variant="body2" color="textSecondary">
                                This act hasn't uploaded any documents yet.
                            </Typography>
                        </div>
                    ) : (
                        <div className="documents-list">
                            {documents.map((doc, index) => (
                                <div key={doc.id || index} className="doc-list-item">
                                    <div 
                                        className="doc-list-icon"
                                        style={{ backgroundColor: this.getFileIconColor(doc.fileName) }}
                                    >
                                        <i className={`fa ${this.getFileIcon(doc.fileName)}`}></i>
                                    </div>
                                    <div className="doc-list-info">
                                        <Typography variant="subtitle1" className="doc-list-title">
                                            {doc.title}
                                        </Typography>
                                        <Typography variant="caption" className="doc-list-meta">
                                            <span className="type-badge custom">
                                                <i className="fa fa-file"></i> Document
                                            </span>
                                            {doc.fileSize && (
                                                <span className="meta-separator">
                                                    {this.formatFileSize(doc.fileSize)}
                                                </span>
                                            )}
                                        </Typography>
                                    </div>
                                    <div className="doc-list-actions">
                                        <IconButton 
                                            onClick={() => this.handleViewDocument(doc)}
                                            className="action-btn view"
                                            title="View"
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => this.handleDownloadDocument(doc)}
                                            className="action-btn download"
                                            title="Download"
                                        >
                                            <CloudDownloadIcon />
                                        </IconButton>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>

                <DialogActions className="modal-actions">
                    {documents.length > 0 && (
                        <Button
                            onClick={this.handleDownloadAll}
                            variant="contained"
                            color="primary"
                        >
                            <CloudDownloadIcon style={{ marginRight: 8, fontSize: 18 }} />
                            Download All
                        </Button>
                    )}
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default ActDocumentsModal;
